import { Suspense } from 'react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useUnitDiscountQuery } from '@/api/accommodation/hooks'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { trackReservationCalendarOpened } from '@/lib/track'
import { useTrackOnce } from '@/hooks/useTrackOnce'
import Price from '@/components/money/price'
import Discount from '@/components/money/discount'
import { LocationInquirySheet } from '../inquiry/LocationInquirySheet'
import { LocationWatchmanSheet } from '../location-watchman/LocationWatchmanSheet'
import { PriceGuaranteePanel } from '../price-guarantee/PriceGuaranteePanel'
import { RequestBookingPanel } from './RequestBookingPanel'
import { DateSelectionCalendar } from './DateSelectionCalendar'
import { DateSelectionSummary } from './DateSelectionSummary'
import { LastMinuteOfferBanner } from './LastMinuteOfferBanner'
import { DateSelectionCalendarSkeleton } from './DateSelectionCalendarSkeleton'
import { DateSelectionSummarySkeleton } from './DateSelectionSummarySkeleton'

interface DateSelectionDesktopProps {
  accommodation: AccommodationDetail
}

export function DateSelectionDesktop({ accommodation }: DateSelectionDesktopProps) {
  const { discount } = useUnitDiscountQuery(accommodation.unit.id)

  useTrackOnce(
    () => {
      const prices = {
        dailyStandardPriceFrom: discount?.maxPrice,
        dailyDiscountPriceFrom: discount?.minPrice,
      }
      trackReservationCalendarOpened(accommodation, prices)
    },
    [accommodation, discount],
    !!discount,
  )

  return (
    <ErrorBoundary title="Chyba při načítání kalendáře" message="Nepodařilo se načíst dostupnost termínů.">
      {discount && (
        <Suspense fallback={null}>
          <LastMinuteOfferBanner accommodation={accommodation} className="mb-4" />
        </Suspense>
      )}

      {discount && (
        <>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xl font-semibold text-slate-900">
                od <Price price={discount.minPrice} /> / noc
              </div>
              {discount.maxPrice > discount.minPrice && (
                <div className="text-sm text-slate-400">
                  <Price price={discount.maxPrice} className="line-through" /> / noc
                </div>
              )}
            </div>
            {discount.maxPrice > discount.minPrice && (
              <Discount minPrice={discount.minPrice} maxPrice={discount.maxPrice} />
            )}
          </div>
          <hr className="my-4 border-slate-200" />
        </>
      )}

      <Suspense fallback={<DateSelectionCalendarSkeleton />}>
        <DateSelectionCalendar accommodation={accommodation} />
      </Suspense>

      <hr className="my-4 border-slate-200" />

      <div className="flex items-center justify-between gap-4">
        <Suspense fallback={<DateSelectionSummarySkeleton />}>
          <DateSelectionSummary accommodation={accommodation} />
        </Suspense>
      </div>

      <div className="mt-4 space-y-3">
        <RequestBookingPanel accommodation={accommodation} />
        <PriceGuaranteePanel accommodation={accommodation} />
        {/* Skipped when there is no availability at all — the calendar's empty
            state already carries its own watchman CTA. */}
        {discount && <LocationWatchmanSheet accommodation={accommodation} variant="panel" />}
      </div>

      <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-4">
        <LocationInquirySheet accommodation={accommodation} />
      </div>
    </ErrorBoundary>
  )
}
